import React, { useEffect, useState } from 'react';
import FilterBar, { FilterField } from 'components/Common/FilterBar';
import { Select, Tag, Space, Badge, Tooltip, DatePicker } from 'antd';
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import { MailOutlined, WhatsAppOutlined, MessageOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import useUrlFilters from 'hooks/useUrlFilters';

dayjs.extend(isBetween);

const { Option } = Select;
const { RangePicker } = DatePicker;

// Filters, search and paging live in the URL so a filtered view can be shared as a link.
const URL_FILTERS = {
  search: { type: 'string' },
  channel: { type: 'string' },
  status: { type: 'string' },
  dates: { type: 'dateRange', keys: ['start_date', 'end_date'] },
  page: { type: 'number', default: 1 },
  pageSize: { type: 'number', key: 'page_size', default: 50 },
};

const ReminderLogsTable = () => {
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  // Filters
  const [filters, setFilters] = useUrlFilters(URL_FILTERS);
  // Any filter change goes back to the first page.
  const urlSetter = (name) => (value) => setFilters({ [name]: value, page: 1 });
  const searchTerm = filters.search || "";
  const setSearchTerm = urlSetter('search');
  const [searchTermLive, setSearchTermLive] = useState(searchTerm);
  useEffect(() => setSearchTermLive(searchTerm), [searchTerm]);
  const channelFilter = filters.channel || undefined;
  const setChannelFilter = urlSetter('channel');
  const statusFilter = filters.status || undefined;
  const setStatusFilter = urlSetter('status');
  const dateRange = filters.dates;
  const setDateRange = urlSetter('dates');

  // Search sits outside the filter panel, so it is neither counted nor cleared.
  const activeFilterCount =
    (channelFilter ? 1 : 0) +
    (statusFilter ? 1 : 0) +
    (dateRange && dateRange[0] && dateRange[1] ? 1 : 0);

  const clearFilters = () => {
    setFilters({ channel: null, status: null, dates: null, page: 1 });
  };

  useEffect(() => {
    fetchReminderLogs();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [dataSource, searchTerm, channelFilter, statusFilter, dateRange]);

  const fetchReminderLogs = async () => {
    setLoading(true);
    try {
      // Paged client-side, so load up to the largest page size on offer.
      const response = await axios.get('/reminders/logs/', {
        params: {
          page_size: MAX_PAGE_SIZE
        }
      });
      if (response.status === 200) {
        setDataSource(response.data.results || response.data || []);
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
    }
    setLoading(false);
  };

  const applyFilters = () => {
    let filtered = [...dataSource];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.recipient_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.recipient_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.recipient_phone?.includes(searchTerm)
      );
    }

    // Channel filter
    if (channelFilter) {
      filtered = filtered.filter(item => item.channel === channelFilter);
    }

    // Status filter
    if (statusFilter) {
      filtered = filtered.filter(item => item.status === statusFilter);
    }

    // Date range filter
    if (dateRange && dateRange[0] && dateRange[1]) {
      filtered = filtered.filter(item => {
        const itemDate = dayjs(item.sent_at || item.created_at);
        return itemDate.isBetween(dateRange[0], dateRange[1], 'day', '[]');
      });
    }

    setFilteredData(filtered);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      PENDING: { status: 'default', text: 'Pending' },
      SENT: { status: 'processing', text: 'Sent' },
      DELIVERED: { status: 'success', text: 'Delivered' },
      READ: { status: 'success', text: 'Read' },
      FAILED: { status: 'error', text: 'Failed' },
      BOUNCED: { status: 'error', text: 'Bounced' },
      UNSUBSCRIBED: { status: 'default', text: 'Unsubscribed' },
    };

    const config = statusConfig[status] || { status: 'default', text: status };
    return <Badge status={config.status} text={config.text} />;
  };

  const getChannelIcon = (channel) => {
    const icons = {
      EMAIL: <MailOutlined style={{ color: '#1890ff' }} />,
      WHATSAPP: <WhatsAppOutlined style={{ color: '#25D366' }} />,
      SMS: <MessageOutlined style={{ color: '#722ed1' }} />,
    };
    return icons[channel] || null;
  };

  const columns = [
    {
      title: 'Sent At',
      dataIndex: 'sent_at',
      key: 'sent_at',
      width: 160,
      render: (text) => text ? dayjs(text).format('YYYY-MM-DD HH:mm') : '-',
      sorter: (a, b) => dayjs(a.sent_at).unix() - dayjs(b.sent_at).unix(),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Entity',
      key: 'entity',
      width: 180,
      render: (_, record) => (
        <div>
          <Tag color="cyan">{record.entity_type?.toUpperCase()}</Tag>
          <div style={{ fontSize: '12px', color: '#888' }}>{record.entity_display}</div>
        </div>
      ),
    },
    {
      title: 'Recipient',
      key: 'recipient',
      width: 200,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.recipient_name}</div>
          <div style={{ fontSize: '12px', color: '#888' }}>
            {record.recipient_email || record.recipient_phone || record.recipient_whatsapp}
          </div>
        </div>
      ),
    },
    {
      title: 'Channel',
      dataIndex: 'channel',
      key: 'channel',
      width: 100,
      render: (channel) => (
        <Space>
          {getChannelIcon(channel)}
          <span>{channel}</span>
        </Space>
      ),
    },
    {
      title: 'Subject/Content',
      dataIndex: 'subject',
      key: 'subject',
      width: 250,
      ellipsis: {
        showTitle: false,
      },
      render: (subject, record) => (
        <Tooltip placement="topLeft" title={subject || record.message_body}>
          {subject || (record.message_body?.substring(0, 50) + '...')}
        </Tooltip>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => getStatusBadge(status),
    },
    {
      title: 'Delivered',
      dataIndex: 'delivered_at',
      key: 'delivered_at',
      width: 140,
      render: (text) => text ? dayjs(text).format('YYYY-MM-DD HH:mm') : '-',
    },
    {
      title: 'Read',
      dataIndex: 'read_at',
      key: 'read_at',
      width: 140,
      render: (text) => text ? dayjs(text).format('YYYY-MM-DD HH:mm') : '-',
    },
    {
      title: 'Retries',
      dataIndex: 'retry_count',
      key: 'retry_count',
      width: 80,
      render: (count) => count > 0 ? <Tag color="orange">{count}</Tag> : '-',
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '16px' }}>
        <h2>Reminder Logs</h2>
      </div>

      <FilterBar
        searchValue={searchTermLive}
        onSearchChange={(value) => { setSearchTermLive(value); setSearchTerm(value); }}
        onSearch={setSearchTerm}
        searchPlaceholder="Search recipient"
        activeCount={activeFilterCount}
        onClear={clearFilters}
        title="Filter Logs"
        onRefresh={() => fetchReminderLogs()}
        refreshing={loading}
      >
        <FilterField label="Channel">
          <Select
            placeholder="Channel"
            value={channelFilter}
            onChange={setChannelFilter}
            allowClear
          >
            <Option value="EMAIL">Email</Option>
            <Option value="WHATSAPP">WhatsApp</Option>
            <Option value="SMS">SMS</Option>
          </Select>
        </FilterField>

        <FilterField label="Status">
          <Select
            placeholder="Status"
            value={statusFilter}
            onChange={setStatusFilter}
            allowClear
          >
            <Option value="PENDING">Pending</Option>
            <Option value="SENT">Sent</Option>
            <Option value="DELIVERED">Delivered</Option>
            <Option value="READ">Read</Option>
            <Option value="FAILED">Failed</Option>
            <Option value="BOUNCED">Bounced</Option>
          </Select>
        </FilterField>

        <FilterField label="Date Range">
          <RangePicker
            format="YYYY-MM-DD"
            value={dateRange}
            onChange={(dates) => setDateRange(dates)}
          />
        </FilterField>
      </FilterBar>

      <Table
        columns={columns}
        dataSource={filteredData}
        loading={loading}
        rowKey="id"
        scroll={{ x: 1400 }}
        pagination={{
          current: filters.page,
          pageSize: Math.min(filters.pageSize, MAX_PAGE_SIZE),
          onChange: (page, size) => setFilters({ page: size !== filters.pageSize ? 1 : page, pageSize: size }),
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} logs`,
        }}
      />
    </div>
  );
};

export default ReminderLogsTable;
